import { parseTelegramMarkdown } from './telegramMarkdown.js'

function stripBrAroundCodeBlocks(html) {
  return html.replace(/<br>\s*(<pre><code>)/g, '$1').replace(/(<\/code><\/pre>)\s*<br>/g, '$1')
}

function stripRedundantBrAroundBlockTags(html) {
  return html
    .replace(/(<\/(?:ul|ol|pre|blockquote)>)(?:\s*<br>)+/g, '$1')
    .replace(/(?:<br>\s*)+(<(?:ul|ol|pre|blockquote)\b[^>]*>)/g, '$1')
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeWhitespace(text) {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function processHtmlNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || ''
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return ''
  }

  const tagName = node.tagName.toLowerCase()
  const children = Array.from(node.childNodes).map(processHtmlNode).join('')

  switch (tagName) {
    case 'img': {
      const src = node.getAttribute('src') || ''
      const alt = node.getAttribute('alt') || node.getAttribute('title') || ''
      return src ? `![${alt}](${src})` : ''
    }
    case 'b':
    case 'strong':
      return `*${children}*`
    case 'i':
    case 'em':
      return `_${children}_`
    case 'u':
      return `__${children}__`
    case 's':
    case 'strike':
    case 'del':
      return `~${children}~`
    case 'code':
      if (node.parentElement?.tagName.toLowerCase() === 'pre') {
        return children
      }
      return `\`${children}\``
    case 'pre':
      return `\`\`\`${children}\`\`\`\n`
    case 'a': {
      const href = node.getAttribute('href') || ''
      return `[${children}](${href})`
    }
    case 'blockquote':
      return children
        .split('\n')
        .map(line => (line ? `> ${line}` : ''))
        .join('\n')
        .concat('\n')
    case 'li': {
      const item = children.replace(/\n+$/g, '').trim()
      return item ? `- ${item}\n` : ''
    }
    case 'ul':
    case 'ol':
      return `${children}\n`
    case 'span':
      if (node.classList.contains('spoiler')) {
        return `||${children}||`
      }
      return children
    case 'br':
      return '\n'
    case 'div':
    case 'p':
      if (node.parentElement?.tagName.toLowerCase() === 'li') {
        return children
      }
      return `${children}\n`
    default:
      return children
  }
}

function renderInlineMarkdown(text, options = {}) {
  return parseTelegramMarkdown(text, {
    processTaskMentions: options.processTaskMentions === true,
    taskMap: options.taskMap || {},
    buildTaskUrl: options.buildTaskUrl,
  })
}

export function telegramMarkdownToHtml(markdown, options = {}) {
  if (!markdown) return ''
  const lines = String(markdown).split('\n')
  const blocks = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const singleLineCodeFenceMatch = line.match(/^```(.*)```$/)
    const fenceStartMatch = line.match(/^```(.*)$/)
    const bulletMatch = line.match(/^\s*-\s+(.+)$/)
    const orderedMatch = line.match(/^\s*\d+\.\s+(.+)$/)

    if (singleLineCodeFenceMatch) {
      blocks.push(`<pre><code>${escapeHtml(singleLineCodeFenceMatch[1])}</code></pre>`)
      index += 1
      continue
    }

    if (fenceStartMatch) {
      index += 1
      const codeLines = []
      const fenceHeader = fenceStartMatch[1] || ''
      const looksLikeLanguageTag = /^[a-zA-Z0-9_-]+$/.test(fenceHeader.trim())
      if (fenceHeader.trim() && !looksLikeLanguageTag) {
        codeLines.push(fenceHeader)
      }
      while (index < lines.length && !lines[index].match(/^```\s*$/)) {
        const closeMarkerIndex = lines[index].indexOf('```')
        if (closeMarkerIndex >= 0) {
          const lineBeforeClose = lines[index].slice(0, closeMarkerIndex)
          if (lineBeforeClose) {
            codeLines.push(lineBeforeClose)
          }
          index += 1
          break
        }
        codeLines.push(lines[index])
        index += 1
      }
      if (index < lines.length && lines[index].match(/^```\s*$/)) {
        index += 1
      }
      blocks.push(`<pre><code>${escapeHtml(codeLines.join('\n'))}</code></pre>`)
      continue
    }

    if (bulletMatch) {
      const items = []
      while (index < lines.length) {
        const match = lines[index].match(/^\s*-\s+(.+)$/)
        if (!match) break
        items.push(renderInlineMarkdown(match[1], options))
        index += 1
      }
      blocks.push(`<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>`)
      continue
    }

    if (orderedMatch) {
      const items = []
      while (index < lines.length) {
        const match = lines[index].match(/^\s*\d+\.\s+(.+)$/)
        if (!match) break
        items.push(renderInlineMarkdown(match[1], options))
        index += 1
      }
      blocks.push(`<ol>${items.map(item => `<li>${item}</li>`).join('')}</ol>`)
      continue
    }

    if (!line.trim()) {
      blocks.push('<br>')
      index += 1
      continue
    }

    blocks.push(renderInlineMarkdown(line, options))
    index += 1
  }

  return stripRedundantBrAroundBlockTags(stripBrAroundCodeBlocks(blocks.join('<br>')))
}

export function htmlToTelegramMarkdown(html) {
  if (!html) return ''
  const temp = document.createElement('div')
  temp.innerHTML = html
  return normalizeWhitespace(processHtmlNode(temp))
}

export function normalizeTelegramMarkdown(markdown) {
  return normalizeWhitespace(markdown)
}
